import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';

/**
 * Per-query record of whether the most recent REST snapshot fetch started
 * after concrete JetStream interest existed for the owning hook's subjects.
 *
 * An eager snapshot fetch starts before the wire installs. When readiness
 * lands while that fetch is still in flight, a plain refetch deduplicates
 * onto it (TanStack shares the in-flight promise) and would fence the
 * pre-snapshot buffer on data that predates delivery. The handoff consults
 * this record to choose between trusting the fetch it dedupes onto and
 * forcing a fresh post-wire one. Keyed per QueryClient and query so sibling
 * observers of one query (a fetch-once owner deduping onto an eager
 * sibling's request) share the answer; entries die with their query.
 */
interface SnapshotFetchRecord {
  /** Handoff scope (query scope + wire version) the fetch started under. */
  readonly scope: string;
  readonly postWire: boolean;
}

const records = new WeakMap<QueryClient, Map<string, SnapshotFetchRecord>>();

function recordsFor(queryClient: QueryClient): Map<string, SnapshotFetchRecord> {
  const existing = records.get(queryClient);
  if (existing !== undefined) return existing;
  const created = new Map<string, SnapshotFetchRecord>();
  records.set(queryClient, created);
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type === 'removed') created.delete(event.query.queryHash);
  });
  return created;
}

export function recordSnapshotFetchStart(
  queryClient: QueryClient,
  queryKey: QueryKey,
  scope: string,
  postWire: boolean,
): void {
  recordsFor(queryClient).set(hashKey(queryKey), { scope, postWire });
}

/**
 * `null` until a snapshot fetch has started for this query; `false` when the
 * last fetch predates delivery on the given scope (it started before concrete
 * interest, or under an earlier wire that has since been replaced).
 */
export function lastSnapshotFetchWasPostWire(
  queryClient: QueryClient,
  queryKey: QueryKey,
  scope: string,
): boolean | null {
  const record = records.get(queryClient)?.get(hashKey(queryKey));
  if (record === undefined) return null;
  return record.scope === scope && record.postWire;
}
